import {
  type ComponentType,
  type FC,
  type MutableRefObject,
  useEffect,
  useRef,
} from 'react';

import { UNSAVED_CHANGES_MESSAGE } from '../../../constants/data';
import { HIDE_ANIMATION_TIME } from '../../../constants/modals';
import {
  clearClosingModal,
  closeModal,
} from '../../../ducks/modals_manager/slice';
import type { ModalsManagerModalAppState } from '../../../ducks/modals_manager/types';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import ErrorBoundary from '../../error_boundary/error_boundary';
import type { ModalComponentProps } from './types';

const getIsOpen = (
  state: Record<string, ModalsManagerModalAppState>,
  modalId: string,
): boolean => state[modalId]?.isOpen ?? false;

const getIsClosing = (
  state: Record<string, ModalsManagerModalAppState>,
  modalId: string,
): boolean => state[modalId]?.closing ?? false;

export type ModalsDefinition = {
  [modalId: string]: ComponentType<ModalComponentProps>;
};

type ModalWrapperProps = {
  modalId: string;
  isOpen: boolean;
  isClosing: boolean;
  modalComponent: ComponentType<ModalComponentProps>;
  closingModalRef: MutableRefObject<string | undefined>;
};

const ModalWrapper: FC<ModalWrapperProps> = ({
  modalId,
  isOpen,
  isClosing,
  modalComponent,
  closingModalRef,
}) => {
  const dispatch = useAppDispatch();

  if (!isOpen && !isClosing) {
    return null;
  }

  if (isClosing) {
    closingModalRef.current = modalId;
  }
  const ModalComponent = modalComponent;
  return (
    <ErrorBoundary key={modalId}>
      <ModalComponent
        modalId={modalId}
        isOpen={isOpen}
        onCancel={(event, options?: { warn: boolean }) => {
          if (
            event?.target instanceof HTMLElement &&
            event.target.tagName === 'INPUT'
          ) {
            // avoid closing modal when hitting esc on components
            // like dropdown or calendars.
            return;
          }
          if (options?.warn) {
            if (window.confirm(UNSAVED_CHANGES_MESSAGE)) {
              dispatch(closeModal({ id: modalId }));
            }
          } else {
            dispatch(closeModal({ id: modalId }));
          }
        }}
        onConfirm={() => {
          dispatch(closeModal({ id: modalId }));
          return Promise.resolve();
        }}
      />
    </ErrorBoundary>
  );
};

type Props = {
  modals: ModalsDefinition;
};

const ModalsManager: FC<Props> = (props) => {
  const { modals } = props;

  const modalsState = useAppSelector((state) => state.modalsManager);

  const closingModal = useRef<string>(undefined);

  const timer = useRef<number>(undefined);

  const dispatch = useAppDispatch();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (closingModal.current && modalsState[closingModal.current]?.closing) {
      timer.current = window.setTimeout(() => {
        if (closingModal.current) {
          dispatch(clearClosingModal({ id: closingModal.current }));
        }
        window.clearTimeout(timer.current);
      }, HIDE_ANIMATION_TIME);
    }
    if (closingModal.current && !modalsState[closingModal.current]?.closing) {
      closingModal.current = undefined;
    }
  }, [modalsState]);

  return Object.entries(modals).map(([modalId, ModalComponent]) => {
    const isOpen = getIsOpen(modalsState, modalId);
    const isClosing = getIsClosing(modalsState, modalId);

    return (
      <ModalWrapper
        key={modalId}
        modalId={modalId}
        isOpen={isOpen}
        isClosing={isClosing}
        modalComponent={ModalComponent}
        closingModalRef={closingModal}
      />
    );
  });
};

export default ModalsManager;
